import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useToast } from '../../components/common';
import NucleusPage, { Verb, FindField } from '../../components/nucleus/NucleusPage';
import DataTable from '../../components/nucleus/DataTable';
import { TitledSet } from '../../components/nucleus/SetHead';
import { Empty, ShowMore } from '../../components/nucleus/Empty';
import { useOpen } from '../../components/nucleus/Inspector';
import { useConfirm } from '../../components/nucleus/actions';
import { usePlace } from '../../nucleus/place';
import { useAuth } from '../../contexts/AuthContext';
import { useSpaceAccess } from '../../nucleus/access';
import NoAccess from '../../components/nucleus/NoAccess';
import { schemaOf, titleOf, daysUntil } from '../nucleus/GraphModel';
import NodeInspector, { trasherOf } from './NodeInspector';
import { loadTrash, restoreNode, purgeNode, emptyTrash } from './graphData';
import { ago, plural, fmt } from '../../nucleus/format';

// TRASH — what left the graph, and can come back. Restore brings a node back
// with the links it had; Delete forever is the one thing here that can't be
// undone, and so is emptying the trash.
const when = (iso) => (iso ? new Date(iso).getTime() : null);

export default function TrashPage() {
    const { groupId: group, spaceName, inSpace, section, page } = usePlace();
    const toast = useToast();
    const confirm = useConfirm();
    const { user } = useAuth();
    const userId = user?.id || user?.user_id;
    // restoring and deleting forever take the row's own `can.delete` (the realm's
    // scope, or having written it); emptying the whole trash takes the verb (it
    // reaches everyone's)
    const access = useSpaceAccess();
    const may = (n) => (access.canOn('delete', n) ? {} : { disabled: true, hint: access.hint });
    const [openId, open, close] = useOpen();
    const [data, setData] = useState(null);            // { nodes, total, autoPurge }
    const [error, setError] = useState(null);
    const [find, setFind] = useState('');
    const [tick, setTick] = useState(0);
    const reload = useCallback(() => setTick((t) => t + 1), []);

    useEffect(() => {
        let live = true;
        loadTrash(group).then((r) => { if (live) { setData(r); setError(null); } }).catch((e) => { if (live) setError(e?.message || 'The trash didn’t load.'); });
        return () => { live = false; };
    }, [group, tick]);
    const more = async () => {
        const r = await loadTrash(group, data.nodes.length);
        setData((d) => ({ ...d, total: r.total, nodes: [...d.nodes, ...r.nodes.filter((n) => !d.nodes.some((x) => x.id === n.id))] }));
    };

    const nodes = data?.nodes || [];
    // A LINK IS A NODE, and it shows only while both its ends do: a restored link
    // whose other end is still in the trash comes back hidden. These are the ends
    // of `n` (a link) that are in the trash, by title.
    const endsInTrash = (n) => {
        if (!['relationship', 'belongs_to'].includes(schemaOf(n))) return [];
        const vj = n.value_json || {};
        const ids = [...(Array.isArray(vj.from) ? vj.from : [vj.from]), ...(Array.isArray(vj.to) ? vj.to : [vj.to])]
            .filter(Boolean).map(String);
        return nodes.filter((x) => x.id !== n.id && ids.includes(String(x.id))).map(titleOf);
    };
    const q = find.trim().toLowerCase();
    const hit = (n) => !q || titleOf(n).toLowerCase().includes(q) || schemaOf(n).toLowerCase().includes(q);

    const actionsOf = (n) => [
        {
            id: 'restore', label: 'Restore', primary: true, ...may(n),
            run: async () => {
                let res;
                try {
                    res = await restoreNode(group, n.id);
                } catch (err) {
                    if (err?.code !== 'LEGACY_TRASH') throw err;
                    const ok = await confirm({
                        title: `Restore “${titleOf(n)}” by itself?`,
                        body: 'It was trashed before restore data was kept, so its links and keys are gone. The node itself comes back.',
                        label: 'Restore it alone',
                        danger: false,
                    });
                    if (!ok) return;
                    res = await restoreNode(group, n.id, { force: true });
                }
                const back = res?.edges_restored || 0;
                const left = res?.edges_skipped || [];
                const waiting = left.filter((e) => e?.recoverable).length;
                const gone = left.length - waiting;
                const hidden = endsInTrash(n);
                toast.success?.([
                    `Restored “${titleOf(n)}”${back ? ` with ${plural(back, 'link')}` : ''}.`,
                    waiting ? `${plural(waiting, 'link')} come${waiting === 1 ? 's' : ''} back when the other end is restored from the trash.` : '',
                    gone ? `${plural(gone, 'link')} stayed behind: the other end is gone.` : '',
                    hidden.length ? `It stays hidden until ${hidden.map((t) => `“${t}”`).join(' and ')} ${hidden.length === 1 ? 'is' : 'are'} restored too: a link shows only while both its ends do.` : '',
                ].filter(Boolean).join(' '));
                if (String(openId) === String(n.id)) close();
                reload();
            },
        },
        {
            id: 'purge', label: 'Delete forever', danger: true, ...may(n),
            confirm: {
                title: `Delete “${titleOf(n)}” forever?`,
                body: 'It leaves the trash for good, with its history. This can’t be undone.',
                label: 'Delete forever',
            },
            run: async () => {
                await purgeNode(group, n.id);
                toast.success?.(`“${titleOf(n)}” is deleted for good.`);
                if (String(openId) === String(n.id)) close();
                reload();
            },
        },
    ];

    const empty = async () => {
        const ok = await confirm({
            title: 'Empty the trash?',
            body: `All ${plural(data.total, 'thing')} in ${spaceName}’s trash ${data.total === 1 ? 'is' : 'are'} deleted for good, with ${data.total === 1 ? 'its' : 'their'} history. This can’t be undone.`,
            label: 'Delete forever',
            danger: true,
        });
        if (!ok) return;
        close();
        const r = await emptyTrash(group);
        if (r.failed) toast.error?.(`Deleted ${fmt(r.done)}; ${fmt(r.failed)} couldn’t be deleted and stayed in the trash. ${r.first || ''}`);
        else toast.success?.(`Deleted ${plural(r.done, 'thing')} for good.`);
        reload();
    };

    // Missions, Sam, You: only when it is known who
    const byWhom = (n) => (!n.trashed_by_app_name && !n.trashed_by_user && !n.trashed_by_user_id ? null : trasherOf(n, userId, !!group));
    const current = nodes.find((n) => String(n.id) === String(openId)) || null;
    const soon = (n) => (data?.autoPurge ? daysUntil(n.purge_after) : null);

    // find dims a row (its title cell carries the mark), never hides it
    const columns = [
        { id: 'item', label: 'Item', lock: true, min: 200, grow: 3, value: titleOf,
          cell: (n) => titleOf(n) },
        { id: 'kind', label: 'Kind', min: 100, grow: 1, value: schemaOf, cell: schemaOf },
        { id: 'trashed', label: 'Trashed', min: 96, grow: 0, asc: false, className: 'gx-cell-when', value: (n) => when(n.deleted_at),
          cell: (n) => ago(n.deleted_at, { dateAfter: 7 }) || '—' },
        { id: 'by', label: 'By', min: 100, grow: 1, value: byWhom, cell: (n) => byWhom(n) || '—' },
        { id: 'links', label: 'Links', num: true, min: 64, grow: 0, value: (n) => n.restorable_edge_count || 0,
          cell: (n) => (n.restorable_edge_count > 0 ? fmt(n.restorable_edge_count) : '—') },
        // only where the trash empties itself
        data?.autoPurge && { id: 'purge', label: 'Purged in', num: true, min: 84, grow: 0, asc: true, value: soon,
          cell: (n) => {
              const days = soon(n);
              return days == null ? '—' : <span className={days < 3 ? 'graph-s-soon' : undefined}>{plural(days, 'day')}</span>;
          } },
    ].filter(Boolean);

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data ? data.total : undefined}
            verbs={<>
                <FindField value={find} onChange={setFind} placeholder="Find in the trash" />
                <Verb icon={<Trash2 />} danger disabled={!data?.total || !access.can('delete')} title={access.can('delete') ? undefined : access.hint} onClick={empty}>Empty trash</Verb>
            </>}
            inspector={current && (
                <NodeInspector key={current.id} node={current} group={group} autoPurge={!!data?.autoPurge} actions={actionsOf(current)} onClose={close} onChanged={reload} />
            )}
        >
            {error && <Empty retry={reload}>{error}</Empty>}
            {data && data.total > 0 && (
                <p className="graph-s-lead">
                    {data.autoPurge
                        ? 'Restore brings a node back with its links. Each is deleted for good on its purge date.'
                        : 'Restore brings a node back with its links. Nothing here is deleted until you say so.'}
                </p>
            )}
            {access.noAccess ? <NoAccess /> : (<>
            <div className="nx-shelf" style={{ paddingTop: 24, paddingBottom: 48 }}>
                <TitledSet title={`Trashed${inSpace}`} count={data ? data.total : undefined} empty={data ? 'The trash is empty.' : undefined}>
                    {nodes.length > 0 && (
                        <DataTable
                            id="graph-trash" label="Trash" columns={columns} rows={nodes} keyOf={(n) => n.id} dimOf={(n) => !hit(n)} total={data?.total}
                            sort={{ id: 'trashed', dir: 'desc' }} currentKey={openId} onOpen={(id) => open(id)} actionsOf={actionsOf}
                        />
                    )}
                </TitledSet>
            </div>
            {data && <ShowMore shown={nodes.length} total={data.total} onMore={more} />}
            </>)}
        </NucleusPage>
    );
}
